import { Button } from '@/components/ui/button';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { CsvColumn, exportToCSV } from '@/lib/csv-utils';
import { airServerErrorFrom } from '@/lib/error-handling';
import { formatInstant, FrequentTimeZones } from '@airreading/common';
import {
  PurgeDataDryRunAssessmentResult,
  PurgeDataDryRunEdlinkUserAssociation,
  PurgeDataDryRunOnDemandGLA,
  PurgeDataDryRunReport,
  PurgeDataDryRunSessionFeedback,
  PurgeDataDryRunSessionFeedbackTimeTracking,
  PurgeDataDryRunStudent,
  PurgeDataDryRunStudentContact,
  PurgeDataDryRunStudentFeedback,
  PurgeDataDryRunStudentGroup,
  PurgeDataDryRunStudentSession,
  PurgeDataDryRunStudentShortLink,
  PurgeDataDryRunStudentStatusAudit,
  PurgeDataDryRunUser,
  PurgeDataDryRunUserIdentity,
  PurgeDataDryRunUserRole,
  PurgeDataDryRunV1UserAssociation,
  PurgeDryRunResult,
  PurgeDryRunTable,
  PurgeDryRunTables,
  SessionFeedbackIssueDetails,
  StudentFeedbackIssueLabelMap,
  StudentFeedbackStatusLabelMap,
  StudentStatusLabelMap,
  studentTagLabel,
  studentYearOptionOnlyNumber,
} from '@airreading/common/data';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const timeZone = FrequentTimeZones.UsCentral;

const formatDateTime = (instant: Parameters<typeof formatInstant>[0]) =>
  formatInstant(instant, timeZone).formattedDateTime;

const formatNullableDateTime = (instant: Parameters<typeof formatInstant>[0] | null) =>
  instant ? formatDateTime(instant) : null;

const schoolReportColumns: CsvColumn<PurgeDataDryRunReport>[] = [
  { id: 'School report ID', getValue: (row) => row.id },
  { id: 'District', getValue: (row) => row.districtName },
  { id: 'School', getValue: (row) => row.schoolName },
  { id: 'School ID', getValue: (row) => row.schoolId },
  { id: 'Student ID', getValue: (row) => row.studentId },
  { id: 'Grade', getValue: (row) => studentYearOptionOnlyNumber(row.grade) },
  { id: 'Report start', getValue: (row) => formatDateTime(row.startedAtInstant) },
  { id: 'Report end', getValue: (row) => formatDateTime(row.endedAtInstant) },
  { id: 'Skill report', getValue: (row) => row.report },
  { id: 'Claude AI skill report', getValue: (row) => row.reportFromAI },
  { id: 'Spanish Claude AI skill report', getValue: (row) => row.reportFromAISpanish },
  { id: 'Session IDs', getValue: (row) => row.sessions.map((session) => session.id).join(', ') },
  {
    id: 'sessionTimes',
    getValue: (row) => row.sessions.map((session) => formatDateTime(session.startInstant)).join(', '),
  },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const sessionColumns = <
  T extends {
    sessionId: string;
    sessionStartInstant: Parameters<typeof formatDateTime>[0];
    sessionDistrictName: string;
    sessionSchoolId: string;
    sessionSchoolName: string;
  },
>(): CsvColumn<T>[] => [
  { id: 'Session ID', getValue: (row) => row.sessionId },
  { id: 'Session start', getValue: (row) => formatDateTime(row.sessionStartInstant) },
  { id: 'Session district', getValue: (row) => row.sessionDistrictName },
  { id: 'Session school', getValue: (row) => row.sessionSchoolName },
  { id: 'Session school ID', getValue: (row) => row.sessionSchoolId },
];

const studentSchoolColumns = <
  T extends { studentSchools: { id: string; name: string; districtName: string }[] },
>(): CsvColumn<T>[] => [
  { id: 'Districts', getValue: (row) => row.studentSchools.map((school) => school.districtName).join(', ') },
  { id: 'Schools', getValue: (row) => row.studentSchools.map((school) => school.name).join(', ') },
  { id: 'School IDs', getValue: (row) => row.studentSchools.map((school) => school.id).join(', ') },
];

const assessmentResultColumns: CsvColumn<PurgeDataDryRunAssessmentResult>[] = [
  { id: 'Assessment result ID', getValue: (row) => row.id },
  ...sessionColumns<PurgeDataDryRunAssessmentResult>(),
  { id: 'Coach ID', getValue: (row) => row.coachId },
  { id: 'Student ID', getValue: (row) => row.studentId },
  { id: 'Assessment', getValue: (row) => row.assessmentName },
  { id: 'Assessment ID', getValue: (row) => row.assessmentId },
  { id: 'Assessment content', getValue: (row) => row.assessmentContentName },
  { id: 'Assessment content ID', getValue: (row) => row.assessmentContentId },
  { id: 'Assessment finished', getValue: (row) => row.assessmentFinished },
  { id: 'Incorrect words', getValue: (row) => row.incorrectWords.join(', ') },
  { id: 'Total words read', getValue: (row) => row.totalWordsRead },
  { id: 'Incorrect words count', getValue: (row) => row.incorrectWordsCount },
  { id: 'Phonemic reading result', getValue: (row) => row.phonemicReadingResult },
  { id: 'Comprehension skill result', getValue: (row) => row.comprehensionSkillResult },
  { id: 'Fluency skill result', getValue: (row) => row.fluencySkillResult },
  { id: 'Comprehension passage result', getValue: (row) => row.comprehensionPassageResult },
  { id: 'Can retell', getValue: (row) => row.canRetell },
  { id: 'Incorrect words read from PM list', getValue: (row) => row.incorrectlWordsReadFromPMList },
  { id: 'Finished early', getValue: (row) => row.finishedEarly },
  { id: 'Total seconds spent', getValue: (row) => row.totalSecondsSpent },
  { id: 'Last updated by ID', getValue: (row) => row.lastUpdatedById },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const sessionFeedbackColumns: CsvColumn<PurgeDataDryRunSessionFeedback>[] = [
  ...sessionColumns<PurgeDataDryRunSessionFeedback>(),
  { id: 'Coach ID', getValue: (row) => row.coachId },
  { id: 'Student ID', getValue: (row) => row.studentId },
  { id: 'Feedback content', getValue: (row) => row.feedbackContent },
  { id: 'Major concerns', getValue: (row) => row.majorConcerns },
  { id: 'Other tech issues', getValue: (row) => row.otherTechIssues },
  {
    id: 'Issues',
    getValue: (row) => row.issues.map((issue) => SessionFeedbackIssueDetails[issue]?.label || issue).join(', '),
  },
  { id: 'Levels', getValue: (row) => row.levels.join(', ') },
  { id: 'Student attended', getValue: (row) => row.studentAttended },
  { id: 'Feedback submitted', getValue: (row) => row.feedbackSubmitted },
  { id: 'Late feedback dismissed', getValue: (row) => row.lateFeedbackDismissed },
  { id: 'Hours from session end to submission', getValue: (row) => row.submittedHoursFromSessionEnd },
  { id: 'Daily summary', getValue: (row) => row.dailySummary },
  { id: 'Submitted at', getValue: (row) => formatNullableDateTime(row.submittedAtInstant) },
  { id: 'Draft saved at', getValue: (row) => formatNullableDateTime(row.draftSavedAtInstant) },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const sessionFeedbackTimeTrackingColumns: CsvColumn<PurgeDataDryRunSessionFeedbackTimeTracking>[] = [
  { id: 'Time tracking ID', getValue: (row) => row.id },
  ...sessionColumns<PurgeDataDryRunSessionFeedbackTimeTracking>(),
  { id: 'Coach ID', getValue: (row) => row.coachId },
  { id: 'Student ID', getValue: (row) => row.studentId },
  { id: 'Actor ID', getValue: (row) => row.actorId },
  { id: 'First started at', getValue: (row) => formatDateTime(row.firstStartedAtInstant) },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentFeedbackColumns: CsvColumn<PurgeDataDryRunStudentFeedback>[] = [
  { id: 'Student feedback ID', getValue: (row) => row.id },
  { id: 'Student ID', getValue: (row) => row.studentId },
  ...studentSchoolColumns<PurgeDataDryRunStudentFeedback>(),
  { id: 'Issue', getValue: (row) => StudentFeedbackIssueLabelMap[row.studentFeedbackIssue] },
  { id: 'Status', getValue: (row) => StudentFeedbackStatusLabelMap[row.status] },
  { id: 'Relevel needed', getValue: (row) => row.relevelNeeded },
  { id: 'Learning path', getValue: (row) => row.learningPathName },
  { id: 'Learning path ID', getValue: (row) => row.learningPathId },
  { id: 'Starting lesson', getValue: (row) => row.startingLessonName },
  { id: 'Starting lesson ID', getValue: (row) => row.startingLessonId },
  { id: 'Reviewer ID', getValue: (row) => row.reviewerId },
  { id: 'Note', getValue: (row) => row.note },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const onDemandGLAColumns: CsvColumn<PurgeDataDryRunOnDemandGLA>[] = [
  { id: 'On-demand GLA ID', getValue: (row) => row.id },
  { id: 'Assigned session ID', getValue: (row) => row.sessionId },
  { id: 'Assigned session start', getValue: (row) => formatDateTime(row.sessionStartInstant) },
  { id: 'District', getValue: (row) => row.sessionDistrictName },
  { id: 'School', getValue: (row) => row.sessionSchoolName },
  { id: 'School ID', getValue: (row) => row.sessionSchoolId },
  { id: 'Student ID', getValue: (row) => row.studentId },
  { id: 'Source session ID', getValue: (row) => row.sourceSessionId },
  { id: 'Source coach ID', getValue: (row) => row.sourceCoachId },
  { id: 'Lesson', getValue: (row) => row.lessonName },
  { id: 'Lesson ID', getValue: (row) => row.lessonId },
  { id: 'Recorded status', getValue: (row) => row.recordedStatus },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentSessionColumns: CsvColumn<PurgeDataDryRunStudentSession>[] = [
  ...sessionColumns<PurgeDataDryRunStudentSession>(),
  { id: 'Student ID', getValue: (row) => row.studentId },
  { id: 'Joined at', getValue: (row) => row.joinedAtInstants.map(formatDateTime).join(', ') },
  { id: 'Left at', getValue: (row) => row.leftAtInstants.map(formatDateTime).join(', ') },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentGroupColumns: CsvColumn<PurgeDataDryRunStudentGroup>[] = [
  { id: 'Student group ID', getValue: (row) => row.id },
  { id: 'District', getValue: (row) => row.districtName },
  { id: 'School', getValue: (row) => row.schoolName },
  { id: 'School ID', getValue: (row) => row.schoolId },
  { id: 'Coach ID', getValue: (row) => row.coachId },
  { id: 'Status', getValue: (row) => row.status },
  // The group sits at the purged school, so it and every membership goes.
  { id: 'Members', getValue: (row) => row.purgedStudentIds.length },
  { id: 'Member student IDs', getValue: (row) => row.purgedStudentIds.join(', ') },
  // Non-zero means some members keep their account through another school.
  { id: 'Members keeping their account', getValue: (row) => row.retainedStudentCount },
  { id: 'Learning path', getValue: (row) => row.learningPathName },
  { id: 'Learning path ID', getValue: (row) => row.learningPathId },
  { id: 'Starting lesson', getValue: (row) => row.startingLessonName },
  { id: 'Starting lesson ID', getValue: (row) => row.startingLessonId },
  { id: 'Attendance status', getValue: (row) => row.attendanceStatus },
  { id: 'Attendance notes', getValue: (row) => row.attendanceNotes },
  { id: 'Notes', getValue: (row) => row.notes },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentContactColumns: CsvColumn<PurgeDataDryRunStudentContact>[] = [
  { id: 'Student contact ID', getValue: (row) => row.id },
  { id: 'Student ID', getValue: (row) => row.studentId },
  ...studentSchoolColumns<PurgeDataDryRunStudentContact>(),
  { id: 'Contact type', getValue: (row) => row.type },
  { id: 'Name', getValue: (row) => row.name },
  { id: 'Email', getValue: (row) => row.email },
  { id: 'Phone', getValue: (row) => row.phone },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentShortLinkColumns: CsvColumn<PurgeDataDryRunStudentShortLink>[] = [
  { id: 'Short code', getValue: (row) => row.shortCode },
  // The short link hangs off the user, whose id is the student's.
  { id: 'User ID', getValue: (row) => row.userId },
  ...studentSchoolColumns<PurgeDataDryRunStudentShortLink>(),
  { id: 'Active', getValue: (row) => row.isActive },
  { id: 'Paused', getValue: (row) => row.isPaused },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentStatusAuditColumns: CsvColumn<PurgeDataDryRunStudentStatusAudit>[] = [
  { id: 'Status audit ID', getValue: (row) => row.id },
  { id: 'Student ID', getValue: (row) => row.studentId },
  ...studentSchoolColumns<PurgeDataDryRunStudentStatusAudit>(),
  { id: 'From status', getValue: (row) => StudentStatusLabelMap[row.fromStatus] },
  { id: 'To status', getValue: (row) => StudentStatusLabelMap[row.toStatus] },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const edlinkUserAssociationColumns: CsvColumn<PurgeDataDryRunEdlinkUserAssociation>[] = [
  { id: 'User ID', getValue: (row) => row.internalId },
  { id: 'Edlink user ID', getValue: (row) => row.externalId },
  ...studentSchoolColumns<PurgeDataDryRunEdlinkUserAssociation>(),
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const v1UserAssociationColumns: CsvColumn<PurgeDataDryRunV1UserAssociation>[] = [
  { id: 'User ID', getValue: (row) => row.internalId },
  { id: 'V1 user ID', getValue: (row) => row.externalId },
  ...studentSchoolColumns<PurgeDataDryRunV1UserAssociation>(),
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const userIdentityColumns: CsvColumn<PurgeDataDryRunUserIdentity>[] = [
  { id: 'User identity ID', getValue: (row) => row.id },
  { id: 'User ID', getValue: (row) => row.userId },
  ...studentSchoolColumns<PurgeDataDryRunUserIdentity>(),
  { id: 'Issuer', getValue: (row) => row.iss },
  { id: 'Subject', getValue: (row) => row.sub },
  { id: 'Status', getValue: (row) => row.status },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const userRoleColumns: CsvColumn<PurgeDataDryRunUserRole>[] = [
  { id: 'User role ID', getValue: (row) => row.id },
  { id: 'User ID', getValue: (row) => row.userId },
  { id: 'Role', getValue: (row) => row.role },
  { id: 'Status', getValue: (row) => row.status },
  { id: 'District', getValue: (row) => row.districtName },
  { id: 'School', getValue: (row) => row.schoolName },
  { id: 'School ID', getValue: (row) => row.schoolId },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const studentColumns: CsvColumn<PurgeDataDryRunStudent>[] = [
  { id: 'Student ID', getValue: (row) => row.id },
  ...studentSchoolColumns<PurgeDataDryRunStudent>(),
  { id: 'Grade', getValue: (row) => studentYearOptionOnlyNumber(row.grade) },
  { id: 'Status', getValue: (row) => (row.status ? StudentStatusLabelMap[row.status] : null) },
  { id: 'FLEID', getValue: (row) => row.fleid },
  { id: 'Special ed', getValue: (row) => row.specialEd },
  { id: 'Dyslexic', getValue: (row) => row.dyslexic },
  { id: 'ELL', getValue: (row) => row.ell },
  { id: 'Classroom teacher', getValue: (row) => row.classroomTeacher },
  { id: 'Tags', getValue: (row) => row.studentTags.map(studentTagLabel).join(', ') },
  { id: 'Learning path', getValue: (row) => row.learningPathName },
  { id: 'Learning path ID', getValue: (row) => row.learningPathId },
  { id: 'Starting lesson', getValue: (row) => row.startingLessonName },
  { id: 'Starting lesson ID', getValue: (row) => row.startingLessonId },
  { id: 'Next session lesson', getValue: (row) => row.nextSessionLessonName },
  { id: 'Next session lesson ID', getValue: (row) => row.nextSessionLessonId },
  { id: 'Requested class time ID', getValue: (row) => row.requestedClassTimeId },
  { id: 'Phonics skill 1 mastered', getValue: (row) => row.phonicsSkill1Mastered },
  { id: 'Phonics skill 2 mastered', getValue: (row) => row.phonicsSkill2Mastered },
  { id: 'Phonics skill 3 mastered', getValue: (row) => row.phonicsSkill3Mastered },
  { id: 'Phonics skill 4 mastered', getValue: (row) => row.phonicsSkill4Mastered },
  { id: 'Morpheme skill 1 mastered', getValue: (row) => row.morphemeSkill1Mastered },
  { id: 'Morpheme skill 2 mastered', getValue: (row) => row.morphemeSkill2Mastered },
  { id: 'Morpheme skill 3 mastered', getValue: (row) => row.morphemeSkill3Mastered },
  { id: 'Average attendance last 14 days', getValue: (row) => row.averageAttendanceLast14Days },
  {
    id: 'Average attendance calculated at',
    getValue: (row) => formatNullableDateTime(row.averageAttendanceLast14DaysCalculatedAtInstant),
  },
  { id: 'Total future sessions', getValue: (row) => row.totalFutureSessions },
  { id: 'Next session date', getValue: (row) => row.nextSessionPlainDate },
  { id: 'GLA follow-up status', getValue: (row) => row.glaFollowUpStatus },
  { id: 'GLA reviewer ID', getValue: (row) => row.glaReviewerId },
  { id: 'GLA review notes', getValue: (row) => row.glaReviewNotes },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

const userColumns: CsvColumn<PurgeDataDryRunUser>[] = [
  { id: 'User ID', getValue: (row) => row.id },
  { id: 'First name', getValue: (row) => row.firstName },
  { id: 'Last name', getValue: (row) => row.lastName },
  { id: 'Email', getValue: (row) => row.email },
  { id: 'Phone', getValue: (row) => row.phone },
  { id: 'Time zone', getValue: (row) => row.timeZone },
  ...studentSchoolColumns<PurgeDataDryRunUser>(),
  // A role outside the purged school means deleting this user reaches past it.
  { id: 'All roles', getValue: (row) => row.roles.map((role) => role.role).join(', ') },
  {
    id: 'All role schools',
    getValue: (row) => row.roles.map((role) => role.schoolName ?? 'No school').join(', '),
  },
  { id: 'Issuer', getValue: (row) => row.iss },
  { id: 'Subject', getValue: (row) => row.sub },
  { id: 'Registrable', getValue: (row) => row.isRegistrable },
  { id: 'Created at', getValue: (row) => formatDateTime(row.createdAtInstant) },
  { id: 'Updated at', getValue: (row) => formatDateTime(row.updatedAtInstant) },
];

function downloadCsv(result: PurgeDryRunResult) {
  const date = new Date().toISOString().split('T')[0];
  const filename = `${result.scopeLabel}_${result.table}_${date}_part_${result.part}.csv`;

  switch (result.table) {
    case 'schoolReports':
      exportToCSV(result.rows, schoolReportColumns, filename);
      break;
    case 'assessmentResults':
      exportToCSV(result.rows, assessmentResultColumns, filename);
      break;
    case 'sessionFeedbacks':
      exportToCSV(result.rows, sessionFeedbackColumns, filename);
      break;
    case 'sessionFeedbackTimeTrackings':
      exportToCSV(result.rows, sessionFeedbackTimeTrackingColumns, filename);
      break;
    case 'studentFeedbacks':
      exportToCSV(result.rows, studentFeedbackColumns, filename);
      break;
    case 'onDemandGLAs':
      exportToCSV(result.rows, onDemandGLAColumns, filename);
      break;
    case 'studentSessions':
      exportToCSV(result.rows, studentSessionColumns, filename);
      break;
    case 'studentGroups':
      exportToCSV(result.rows, studentGroupColumns, filename);
      break;
    case 'studentContacts':
      exportToCSV(result.rows, studentContactColumns, filename);
      break;
    case 'studentShortLinks':
      exportToCSV(result.rows, studentShortLinkColumns, filename);
      break;
    case 'studentStatusAudits':
      exportToCSV(result.rows, studentStatusAuditColumns, filename);
      break;
    case 'edlinkUserAssociations':
      exportToCSV(result.rows, edlinkUserAssociationColumns, filename);
      break;
    case 'v1UserAssociations':
      exportToCSV(result.rows, v1UserAssociationColumns, filename);
      break;
    case 'userIdentities':
      exportToCSV(result.rows, userIdentityColumns, filename);
      break;
    case 'userRoles':
      exportToCSV(result.rows, userRoleColumns, filename);
      break;
    case 'students':
      exportToCSV(result.rows, studentColumns, filename);
      break;
    case 'users':
      exportToCSV(result.rows, userColumns, filename);
      break;
  }
}

type TableState =
  | { status: 'queued'; parts: PurgeDryRunResult[] }
  | { status: 'running'; parts: PurgeDryRunResult[] }
  | { status: 'done'; parts: PurgeDryRunResult[] }
  | { status: 'failed'; message: string; parts: PurgeDryRunResult[] };

const initialStates = () =>
  Object.fromEntries(
    PurgeDryRunTables.map((table) => [table, { status: 'queued', parts: [] } as TableState]),
  ) as Record<PurgeDryRunTable, TableState>;

const rowsFetched = (parts: PurgeDryRunResult[]) => parts.reduce((total, { rows }) => total + rows.length, 0);

const hasMoreParts = (parts: PurgeDryRunResult[]) => {
  const last = parts.at(-1);
  return last !== undefined && rowsFetched(parts) < last.totalRows;
};

const partLabel = ({ part, rows, totalRows }: PurgeDryRunResult) =>
  part === 1 && rows.length === totalRows ? 'Full Export' : `Part ${part}`;

interface PurgeDryRunDownloadsProps {
  runDryRun: (table: PurgeDryRunTable, part: number) => Promise<PurgeDryRunResult>;
}

export const PurgeDryRunDownloads = ({ runDryRun }: PurgeDryRunDownloadsProps) => {
  const [states, setStates] = useState<Record<PurgeDryRunTable, TableState>>(initialStates);

  const runDryRunRef = useRef(runDryRun);

  const fetchPart = useCallback(
    async (table: PurgeDryRunTable, part: number, isSuperseded: () => boolean = () => false) => {
      setStates((prev) => ({ ...prev, [table]: { ...prev[table], status: 'running' } }));

      try {
        const result = await runDryRunRef.current(table, part);
        if (isSuperseded()) return;
        setStates((prev) => ({ ...prev, [table]: { status: 'done', parts: [...prev[table].parts, result] } }));
      } catch (e: unknown) {
        if (isSuperseded()) return;
        const message = airServerErrorFrom(e)?.userFacingMessage || 'Dry run failed';
        setStates((prev) => ({ ...prev, [table]: { status: 'failed', message, parts: prev[table].parts } }));
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const isSuperseded = () => cancelled;

    const runAll = async () => {
      for (const table of PurgeDryRunTables) {
        if (cancelled) return;
        await fetchPart(table, 1, isSuperseded);
      }
    };

    void runAll();

    return () => {
      cancelled = true;
    };
  }, [fetchPart]);

  return (
    <div className="w-full" data-testid="purge-dry-run-downloads">
      <h4 className="font-bold text-sm mb-2">Dry run exports</h4>
      <dl className="text-sm">
        {PurgeDryRunTables.map((table) => (
          <div key={table} className="mb-3 last:mb-0" data-testid={`purge-dry-run-row-${table}`}>
            <dt className="font-mono">{table}</dt>
            <TableParts state={states[table]} table={table} onExportPart={fetchPart} />
          </div>
        ))}
      </dl>
    </div>
  );
};

const TableParts = ({
  state,
  table,
  onExportPart,
}: {
  state: TableState;
  table: PurgeDryRunTable;
  onExportPart: (table: PurgeDryRunTable, part: number) => void;
}) => (
  <>
    <FetchedParts parts={state.parts} table={table} />
    {state.status === 'queued' && <dd className="ml-4 text-gray-600">Queued…</dd>}
    {state.status === 'running' && (
      <dd className="ml-4 flex items-center gap-1 text-gray-600">
        <LoaderIcon className="h-3 w-3" />
        Running…
      </dd>
    )}
    {state.status === 'failed' && <dd className="ml-4 text-destructive">{state.message}</dd>}
    {(state.status === 'done' || state.status === 'failed') && (
      <RemainingRows parts={state.parts} table={table} onExportPart={onExportPart} />
    )}
  </>
);

const FetchedParts = ({ parts, table }: { parts: PurgeDryRunResult[]; table: PurgeDryRunTable }) => {
  if (parts.length === 1 && parts[0].totalRows === 0) {
    return <dd className="ml-4 text-gray-600">No rows</dd>;
  }

  return (
    <>
      {parts.map((result) => (
        <dd key={result.part} className="ml-4 flex items-center gap-2">
          <span className="text-gray-600">
            {partLabel(result)}: {result.rows.length.toLocaleString()} {result.rows.length === 1 ? 'row' : 'rows'}
          </span>
          {result.rows.length > 0 && (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 align-baseline"
              icon={Download}
              iconClassName="h-4 w-4"
              onClick={() => downloadCsv(result)}
              data-testid={`purge-dry-run-download-${table}-part-${result.part}`}
            >
              Download CSV
            </Button>
          )}
        </dd>
      ))}
    </>
  );
};

const RemainingRows = ({
  parts,
  table,
  onExportPart,
}: {
  parts: PurgeDryRunResult[];
  table: PurgeDryRunTable;
  onExportPart: (table: PurgeDryRunTable, part: number) => void;
}) => {
  if (!hasMoreParts(parts)) {
    return null;
  }

  const nextPart = parts.length + 1;

  return (
    <dd className="ml-4 flex items-center gap-2">
      <span className="font-medium text-gray-900">
        {rowsFetched(parts).toLocaleString()} of {parts[parts.length - 1].totalRows.toLocaleString()} rows exported
      </span>
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 align-baseline"
        onClick={() => onExportPart(table, nextPart)}
        data-testid={`purge-dry-run-export-part-${table}`}
      >
        Export Part {nextPart}
      </Button>
    </dd>
  );
};
